import type { SnakeCase } from "type-fest";

export const toSnakeCase = (str: string): string =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

/**
 * Blob または Blob[] を除き、キーを再帰的に SnakeCase 化するユーティリティ型
 */
export type SnakeCasedPropertiesDeepWithoutBlob<T> =
  // Blob はそのまま
  T extends Blob
    ? T
    : // Blob[] もそのまま
      T extends Blob[]
      ? T
      : // 配列の場合は、中身を再帰的に SnakeCase 化
        T extends (infer U)[]
        ? SnakeCasedPropertiesDeepWithoutBlob<U>[]
        : // オブジェクトならキーを SnakeCase 化して再帰
          T extends object
          ? {
              [K in keyof T as K extends string
                ? SnakeCase<K>
                : K]: SnakeCasedPropertiesDeepWithoutBlob<T[K]>;
            }
          : // それ以外のプリミティブはそのまま
            T;
/**
 * Blob[] かどうかを判定するためのヘルパー
 */
const isBlobArray = (value: unknown): value is Blob[] =>
  Array.isArray(value) && value.every((item) => item instanceof Blob);

/**
 * 再帰的にオブジェクトのキーをスネークケースに変換する (Blob/Blob[] はスキップ)
 */
const keysToSnakeCaseObject = <T>(
  obj: T,
): SnakeCasedPropertiesDeepWithoutBlob<T> => {
  // Blob または Blob[] の場合はキー変換を行わず、そのまま返す
  if (obj instanceof Blob || isBlobArray(obj)) {
    return obj as SnakeCasedPropertiesDeepWithoutBlob<T>;
  }

  if (Array.isArray(obj)) {
    return obj.map((v) =>
      keysToSnakeCaseObject(v),
    ) as SnakeCasedPropertiesDeepWithoutBlob<T>;
  }

  if (obj !== null && typeof obj === "object" && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => {
        const snakeKey = toSnakeCase(key) as SnakeCase<typeof key & string>;
        const value = (obj as Record<string, unknown>)[key];
        // biome-ignore lint: 再帰的に型を捜査するのを真面目にやるとかなり難しいためany型で妥協する
        (result as any)[snakeKey] = keysToSnakeCaseObject(value);
        return result;
      },
      {} as SnakeCasedPropertiesDeepWithoutBlob<T>,
    );
  }

  return obj as SnakeCasedPropertiesDeepWithoutBlob<T>;
};

const keysToSnakeCaseArray = <T>(
  obj: T[],
): SnakeCasedPropertiesDeepWithoutBlob<T>[] =>
  obj.map((v) => keysToSnakeCaseObject(v));

export function keysToSnakeCase<T>(
  obj: T,
): SnakeCasedPropertiesDeepWithoutBlob<T>;
export function keysToSnakeCase<T>(
  obj: T[],
): SnakeCasedPropertiesDeepWithoutBlob<T>[];
export function keysToSnakeCase<T>(
  obj: T | T[],
):
  | SnakeCasedPropertiesDeepWithoutBlob<T>
  | SnakeCasedPropertiesDeepWithoutBlob<T>[] {
  if (Array.isArray(obj)) {
    return keysToSnakeCaseArray(obj);
  }
  return keysToSnakeCaseObject(obj);
}
